import { EmptyPinnedSlot } from "@/components/chat/chat-empty-pinned-slot";
import { PromptSuggestionCard } from "@/components/chat/chat-prompts-suggestions-card";
import DialogSelectPrompt from "@/components/prompt/dialog-select-prompt";
import { addPromptToHistory } from "@/components/prompt/prompts-popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { MAX_PINNED_PROMPTS } from "@/enums/pinned-prompts";
import { usePinnedPrompts } from "@/hooks/usePromptsPinned";
import { useTranslation } from "@/hooks/useTranslation";
import {
  buildPromptSnapshot,
  getPromptContent,
  getPromptId,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { ConversationMessagePromptInfo, PromptDTO } from "@shaarp/common";
import { useState } from "react";

/** Placeholder cards while the list loads: two rows of the two-column grid. */
const LOADING_CARDS = 4;

/**
 * Column of the company chat's entry screen (#707): the tactics pinned on
 * company pages, in the order that page keeps, each launched in one click. The
 * cards are the ones of the chat's own tactic block (pin badge included, which
 * unpins), laid out on two columns: eight tactics at most, four rows.
 *
 * One dashed slot, the chat's too, offers to pin another tactic while the list
 * has room; with nothing pinned it is the whole column.
 */
export function ChatCompanyTactics({
  onSelect,
  disabled = false,
}: {
  /** The tactic's text to send, and the tactic itself for the message (#681). */
  onSelect: (content: string, prompt: ConversationMessagePromptInfo) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);
  const { toast } = useToast();
  const toastError = useToastError();
  const { pinnedPrompts, isLoading, isPinned, togglePinned } =
    usePinnedPrompts({
      context: "company",
      fetchCustomPrompts: true,
      listenForChanges: true,
    });
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const canAddMore = pinnedPrompts.length < MAX_PINNED_PROMPTS.company;

  const handleSelect = (prompt: PromptDTO) => {
    addPromptToHistory(prompt, undefined, lang);
    onSelect(
      getPromptContent(prompt, lang),
      buildPromptSnapshot(prompt, "", lang),
    );
  };

  const handlePin = (prompt: PromptDTO) => {
    setIsPickerOpen(false);
    // Pinning is a toggle: an already pinned tactic would be unpinned.
    if (isPinned(prompt)) {
      toast({
        title: t("prompt.pinned.alreadyPinned.title"),
        description: t("prompt.pinned.alreadyPinned.description"),
      });
      return;
    }
    // The pin call answers false rather than throwing when it fails.
    togglePinned(prompt).then((isNowPinned) => {
      if (!isNowPinned) toastError();
    });
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-2" aria-busy="true">
        {Array.from({ length: LOADING_CARDS }).map((_, index) => (
          <Skeleton key={index} className="h-[62px] rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {pinnedPrompts.map((prompt, index) => (
          <PromptSuggestionCard
            key={getPromptId(prompt)}
            prompt={prompt}
            index={index}
            isPinned
            isTransitioning={false}
            disabled={disabled}
            onClick={handleSelect}
            onUnpin={togglePinned}
          />
        ))}
        {canAddMore && (
          <EmptyPinnedSlot
            index={pinnedPrompts.length}
            isTransitioning={false}
            disabled={disabled}
            onClick={() => setIsPickerOpen(true)}
          />
        )}
      </div>

      {isPickerOpen && (
        <DialogSelectPrompt
          context="company"
          onSelect={handlePin}
          onClose={() => setIsPickerOpen(false)}
        />
      )}
    </>
  );
}
